import React, { useMemo } from 'react';

import * as Icons from '@tabler/icons-react';

const ButtonPresets = {
  primary: `tw-py-[12px]
            tw-px-[20px]
            md:tw-py-[20px]
            md:tw-px-[36px]
            tw-primaryGradientBorder
            tw-rounded-[52px]
            tw-border-[3px]
            hover:tw-primaryGradientBorderHover
            focus:tw-primaryGradientBorderHover
            active:tw-primaryGradientBorderActive`,

  fab: `tw-py-[12px]
        tw-px-[20px]
        md:tw-py-[20px]
        md:tw-px-[36px]
        tw-bg-periwinkle-700
        tw-rounded-[52px]
        tw-text-white
       active:tw-bg-periwinkle-200
       hover:tw-bg-periwinkle-200
       hover:tw-text-space`,

  fabNoBackground: `tw-py-[12px]
       tw-px-[20px]
       md:tw-py-[20px]
       md:tw-px-[36px]
       tw-rounded-[52px]
       tw-text-white`,

  fabIcon: `tw-p-[16px] 
            tw-bg-periwinkle-700
            tw-rounded-full 
           active:tw-bg-periwinkle-200
           hover:tw-bg-periwinkle-200`,

  noBackground: `tw-bg-transparent
                tw-p-0 
                tw-text-periwinkle-700 
                hover:tw-underline 
                hover:tw-underline-offset-[2px] 
                hover:tw-decoration-[2px]  
                focus:tw-underline 
                focus:tw-underline-offset-[2px] 
                focus:tw-decoration-[2px] 
                active:tw-underline 
                active:tw-underline-offset-[2px] 
                active:tw-decoration-[2px] 
                active:tw-text-periwinkle-200`,
};

interface PrimaryButtonProps
  extends React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > {
  /**
   * Is this the principal call to action on the page?
   */
  preset: 'primary';
  /**
   * Button contents
   */
  text: string;
}

interface ButtonProps
  extends React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > {
  /**
   * Is this the principal call to action on the page?
   */
  preset: Exclude<keyof typeof ButtonPresets, 'primary' | 'noBackground'>;
  /**
   * Button contents
   */
  text?: string;
  /**
   * Name of Tabler Icon
   */
  iconName?: Exclude<keyof typeof Icons, 'createReactComponent'>;
}

interface NoBackgroundButtonProps
  extends React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > {
  /**
   * Is this the principal call to action on the page?
   */
  preset: 'noBackground';
  /**
   * Button contents
   */
  text: string;
  /**
   * Name of Tabler Icon
   */
  iconName?: Exclude<keyof typeof Icons, 'createReactComponent'>;
}

type Props = ButtonProps | PrimaryButtonProps | NoBackgroundButtonProps;

/**
 * Primary UI component for user interaction
 */
const Button = (props: Props) => {
  const IconToRender = useMemo(() => {
    if (props.preset !== 'primary' && props.iconName) {
      return Icons[props.iconName];
    } else {
      return null;
    }
  }, [props]);

  return (
    <button
      type="button"
      {...props}
      className={`${
        ButtonPresets[props.preset]
      } tw-flex tw-flex-row tw-items-center tw-justify-center ${
        props.className
      }`}
    >
      <div
        className={`tw-button ${
          props.text && IconToRender !== null ? 'mr-[8px]' : ''
        }`}
      >
        {props.text}
      </div>
      {IconToRender && <IconToRender size={24} stroke={1.5} />}
    </button>
  );
};

export { Button };
